/**
 * Auto Sequence - Automatic offset calculation for signal coordination
 *
 * Calculates offsets based on travel time between intersections.
 * Can optimize for NB (northbound) or SB (southbound) progression.
 */

import type { Intersection, Phase } from '../types/plan';

export type SequenceDirection = 'NB' | 'SB';

interface GreenWindow {
  startTime: number;  // seconds from cycle start
  duration: number;   // seconds
  phases: string[];   // phase IDs included (e.g., ['A', 'B'])
}

/**
 * Calculate travel time between intersections
 * @param distanceMetres - Distance in metres
 * @param speedKmh - Speed in km/h
 * @returns Travel time in seconds
 */
export function calculateTravelTime(distanceMetres: number, speedKmh: number): number {
  if (speedKmh <= 0) return 0;
  // Convert: (metres / km/h) * 3.6 = seconds
  return (distanceMetres / speedKmh) * 3.6;
}

/**
 * Find the longest green window for a given direction at an intersection
 * A green window can span multiple adjacent phases if they're all applicable to the direction.
 *
 * @param phases - Array of phases for the intersection
 * @param direction - 'NB' or 'SB'
 * @returns The longest green window with start time and duration
 */
export function findLongestGreenWindow(
  phases: Phase[],
  direction: SequenceDirection
): GreenWindow | null {
  const validDirections = direction === 'NB'
    ? ['NB', 'NBSB']
    : ['SB', 'NBSB'];

  let bestWindow: GreenWindow | null = null;
  let currentWindow: GreenWindow | null = null;
  let currentTime = 0;

  for (const phase of phases) {
    const isGreen = validDirections.includes(phase.direction);

    if (isGreen && phase.duration > 0) {
      if (!currentWindow) {
        // Start a new window
        currentWindow = {
          startTime: currentTime,
          duration: phase.duration,
          phases: [phase.id],
        };
      } else {
        // Extend current window
        currentWindow.duration += phase.duration;
        currentWindow.phases.push(phase.id);
      }
    } else {
      // End current window if exists
      if (currentWindow) {
        if (!bestWindow || currentWindow.duration > bestWindow.duration) {
          bestWindow = currentWindow;
        }
        currentWindow = null;
      }
    }

    currentTime += phase.duration;
  }

  // Check if current window at end is the best
  if (currentWindow) {
    if (!bestWindow || currentWindow.duration > bestWindow.duration) {
      bestWindow = currentWindow;
    }
  }

  return bestWindow;
}

/**
 * Find the first green phase for a direction
 *
 * @param phases - Array of phases
 * @param direction - 'NB' or 'SB'
 * @returns Start time of first green phase, or 0 if none found
 */
export function findFirstGreenStart(
  phases: Phase[],
  direction: SequenceDirection
): number {
  const validDirections = direction === 'NB'
    ? ['NB', 'NBSB']
    : ['SB', 'NBSB'];

  let currentTime = 0;
  for (const phase of phases) {
    if (validDirections.includes(phase.direction) && phase.duration > 0) {
      return currentTime;
    }
    currentTime += phase.duration;
  }
  return 0;
}

/**
 * Calculate the cycle length for an intersection
 */
function getCycleLength(phases: Phase[]): number {
  return phases.reduce((sum, p) => sum + p.duration, 0);
}

/**
 * Calculate auto-sequence offsets for all intersections
 *
 * Basic mode: Sets offset based purely on travel time
 * Smart mode: Aligns arrival with the longest green window
 *
 * @param intersections - Array of intersections
 * @param direction - 'NB' or 'SB' progression
 * @param smartMode - If true, align with longest green window; if false, simple travel time
 * @returns Array of calculated offsets (same order as intersections)
 */
export function calculateAutoSequenceOffsets(
  intersections: Intersection[],
  direction: SequenceDirection,
  smartMode: boolean = true
): number[] {
  if (intersections.length === 0) return [];
  if (intersections.length === 1) return [0];

  const offsets: number[] = new Array(intersections.length).fill(0);

  // Get a reference cycle length (use first intersection)
  const cycleLength = getCycleLength(intersections[0].phases);
  if (cycleLength <= 0) return offsets;

  if (direction === 'NB') {
    // Northbound: Start from south (first intersection), progress north
    // First intersection is the reference (offset = 0)
    offsets[0] = 0;

    let cumulativeTime = 0;

    for (let i = 1; i < intersections.length; i++) {
      const prevInt = intersections[i - 1];

      // Travel time from previous intersection to this one
      const travelTime = calculateTravelTime(prevInt.distanceToNext, prevInt.speedNorth);
      cumulativeTime += travelTime;

      if (smartMode) {
        // Find when the longest green window starts at this intersection
        const greenWindow = findLongestGreenWindow(intersections[i].phases, 'NB');
        const greenStart = greenWindow ? greenWindow.startTime : 0;

        // Offset = arrival time - green start time (so we arrive when green starts)
        // Wrap to cycle length
        let offset = (cumulativeTime - greenStart) % cycleLength;
        if (offset < 0) offset += cycleLength;
        offsets[i] = Math.round(offset);
      } else {
        // Simple mode: offset = cumulative travel time
        offsets[i] = Math.round(cumulativeTime % cycleLength);
      }
    }
  } else {
    // Southbound: Start from north (last intersection), progress south
    // Last intersection is the reference (offset = 0)
    const lastIdx = intersections.length - 1;
    offsets[lastIdx] = 0;

    let cumulativeTime = 0;

    for (let i = lastIdx - 1; i >= 0; i--) {
      const nextInt = intersections[i]; // "next" in SB direction is lower index

      // Travel time from this intersection to the next (going south)
      // Distance is stored as distanceToNext (northward), so we use i's distanceToNext
      // But speed is southbound
      const travelTime = calculateTravelTime(nextInt.distanceToNext, nextInt.speedSouth);
      cumulativeTime += travelTime;

      if (smartMode) {
        // Find when the longest green window starts at this intersection
        const greenWindow = findLongestGreenWindow(intersections[i].phases, 'SB');
        const greenStart = greenWindow ? greenWindow.startTime : 0;

        // Offset = arrival time - green start time
        let offset = (cumulativeTime - greenStart) % cycleLength;
        if (offset < 0) offset += cycleLength;
        offsets[i] = Math.round(offset);
      } else {
        // Simple mode: offset = cumulative travel time
        offsets[i] = Math.round(cumulativeTime % cycleLength);
      }
    }
  }

  return offsets;
}

/**
 * Apply auto-sequence offsets to intersections
 * Returns updated intersection array (immutable update)
 */
export function applyAutoSequence(
  intersections: Intersection[],
  direction: SequenceDirection,
  smartMode: boolean = true
): Intersection[] {
  const offsets = calculateAutoSequenceOffsets(intersections, direction, smartMode);

  return intersections.map((int, i) => ({
    ...int,
    offset: offsets[i],
  }));
}
